import React, { createContext, useContext, useState, useEffect } from 'react';
import * as api from '../services/api';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  // Check if user is logged in
  const isAuthenticated = () => {
    return !!localStorage.getItem('token');
  };

  // Load cart from API if authenticated
  const loadCart = async () => {
    if (!isAuthenticated()) {
      // Use local storage for guest cart
      const localCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
      setCart({ items: localCart });
      setCartCount(localCart.reduce((sum, item) => sum + item.quantity, 0));
      return;
    }

    try {
      setLoading(true);
      const cartData = await api.getCart();
      setCart(cartData);
      setCartCount(cartData.items?.reduce((sum, item) => sum + item.quantity, 0) || 0);
    } catch (error) {
      console.error('Error loading cart:', error);
      setCart({ items: [] });
      setCartCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Add to cart
  const addToCart = async (productId, variant, quantity = 1) => {
    if (!isAuthenticated()) {
      // Guest cart - use local storage
      const localCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
      const existingIndex = localCart.findIndex(
        item => item.productId === productId && item.variant?.sku === variant?.sku
      );

      if (existingIndex > -1) {
        localCart[existingIndex].quantity += quantity;
      } else {
        localCart.push({ productId, variant, quantity });
      }

      localStorage.setItem('guestCart', JSON.stringify(localCart));
      setCart({ items: localCart });
      setCartCount(localCart.reduce((sum, item) => sum + item.quantity, 0));
      return;
    }

    try {
      const cartData = await api.addToCart(productId, variant, quantity);
      setCart(cartData);
      setCartCount(cartData.items?.reduce((sum, item) => sum + item.quantity, 0) || 0);
    } catch (error) {
      console.error('Error adding to cart:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add to cart';
      if (error.response?.status === 401) {
        // User not authenticated - redirect to login or use guest cart
        alert('Please log in to add items to cart');
        // Fallback to guest cart
        const localCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
        const existingIndex = localCart.findIndex(
          item => item.productId === productId && item.variant?.sku === variant?.sku
        );
        if (existingIndex > -1) {
          localCart[existingIndex].quantity += quantity;
        } else {
          localCart.push({ productId, variant, quantity, _id: Date.now().toString() });
        }
        localStorage.setItem('guestCart', JSON.stringify(localCart));
        setCart({ items: localCart });
        setCartCount(localCart.reduce((sum, item) => sum + item.quantity, 0));
        return;
      }
      throw new Error(errorMessage);
    }
  };

  // Update cart item
  const updateCartItem = async (itemId, quantity) => {
    if (!isAuthenticated()) {
      const localCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
      const item = localCart.find(item => item._id === itemId);
      if (item) {
        item.quantity = quantity;
        localStorage.setItem('guestCart', JSON.stringify(localCart));
        setCart({ items: localCart });
        setCartCount(localCart.reduce((sum, item) => sum + item.quantity, 0));
      }
      return;
    }

    try {
      const cartData = await api.updateCartItem(itemId, quantity);
      setCart(cartData);
      setCartCount(cartData.items?.reduce((sum, item) => sum + item.quantity, 0) || 0);
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  };

  // Remove from cart
  const removeFromCart = async (itemId) => {
    if (!isAuthenticated()) {
      const localCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
      const filtered = localCart.filter(item => item._id !== itemId);
      localStorage.setItem('guestCart', JSON.stringify(filtered));
      setCart({ items: filtered });
      setCartCount(filtered.reduce((sum, item) => sum + item.quantity, 0));
      return;
    }

    try {
      const cartData = await api.removeFromCart(itemId);
      setCart(cartData);
      setCartCount(cartData.items?.reduce((sum, item) => sum + item.quantity, 0) || 0);
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  };

  // Clear cart
  const clearCart = async () => {
    if (!isAuthenticated()) {
      localStorage.removeItem('guestCart');
      setCart({ items: [] });
      setCartCount(0);
      return;
    }

    try {
      await api.clearCart();
      setCart({ items: [] });
      setCartCount(0);
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  };

  useEffect(() => {
    loadCart();
  }, []);

  // Reload cart when user logs in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadCart();
    }
  }, [localStorage.getItem('token')]);

  const value = {
    cart,
    cartCount,
    loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    loadCart,
    isAuthenticated: isAuthenticated()
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

